import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import Card from "@/design-system/Card/Card";
import { useTokenCosts } from "@/design-system/Evolution/useTokenCosts";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import { MetaRatingChip } from "@/design-system/Tags/MetaRatingChip/MetaRatingChip";
import { CoinIcon, InfoIcon } from "@/designSystemIcons";
import { expire as expireIcon, repeatable } from "@/icons";
import { cn } from "@/lib/utils";
import {
  EvolutionBuilderPlayer,
  chainTagLabel,
  getEvolutionChainTagColor,
} from "@/redux/evolutions/evolutionsBuilderSlice";
import { getEvolutionByIdSelector } from "@/redux/evolutions/evolutionsSlice";
import {
  getTier,
  playerRoleLabelSelector,
  playersSelector,
} from "@/redux/players/playersSlice";
import { BREAKPOINTS } from "@/settings";
import { costTokens, formatEvoCost } from "@repo/shared/evolutions";
import { useTranslation } from "@repo/shared/i18n";
import type {
  EvolutionChainRecommendation,
  EvolutionChainTagType,
  EvolutionStep,
} from "@repo/shared/types";
import { formatTime } from "@repo/shared/utils";
import { type ReactNode, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { EvoChainFromIds } from "../../sections/EvoChainFromIds";
import { ChainWall, type ChainWallMode } from "../../sections/EvolutionChain";
import { useUpgradeChipRows } from "./UpgradeChips";

export type EvoStepCardProps = (
  | EvolutionStep
  | EvolutionChainRecommendation
) & {
  onClick: () => void;
  loading: boolean;
  /** The player after the step, for the card art. Omit to draw no card. */
  player: EvolutionBuilderPlayer | null;
  metaRatingBeforeApplyingUpgrade: number;
  playerRoleIdBeforeApplyingUpgrade: number;
  onClickUnlockChains?: () => void;
  chainWall?: ChainWallMode;
  /** "large" draws the player at full size, for the pathway sheet. */
  size?: "small" | "medium" | "large" | "chain";
  requiredGames?: number;
  className?: string;
};

const CARD_SCALE = { small: 0.6, medium: 0.6, large: 0.8, chain: 0.7 };

/**
 * One evolution step, or a recommended chain, as a pressable card: the
 * chain chips, the meta rating it lands on, what it costs and takes, and the
 * upgrades as chips. Pressing it applies the step; a covered chain shows the
 * wall instead.
 */
export const EvoStepCard = (props: EvoStepCardProps) => {
  const { t } = useTranslation(["evoBuilder", "common", "evolutions"]);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  const {
    upgrades,
    onClick,
    loading,
    player,
    metaRatingBeforeApplyingUpgrade,
    playerRoleIdBeforeApplyingUpgrade,
    onClickUnlockChains,
    chainWall,
    size = "small",
    requiredGames: requiredGamesProp,
    className,
  } = props;

  const isChain = props.kind === "chain";
  const evolutionId = !isChain ? props.evolutionId : undefined;
  const chainIds = useMemo(() => {
    if (!isChain) return undefined;
    if (props.evolutionIds) return props.evolutionIds;
    // Withheld ids still take up their slots, so the wall covers a chain of
    // the right length.
    return Array.from(
      { length: Math.min(props.chainLength ?? 0, 9) },
      () => null as number,
    );
  }, [
    isChain,
    isChain ? props.evolutionIds : undefined,
    isChain ? props.chainLength : 0,
  ]);
  const tags = isChain ? props.tags : undefined;

  const evolution = useSelector(getEvolutionByIdSelector(evolutionId));
  const cost = isChain ? props.cost : evolution?.cost;
  const tokenCosts = useTokenCosts(costTokens(cost));
  const requiredGames = isChain ? requiredGamesProp : evolution?.matches;
  const endTime = isChain ? props.expiresAt : evolution?.endTime;
  const timeRemaining = useMemo(
    () => (endTime ? formatTime(endTime * 1000 - Date.now()) : ""),
    [endTime],
  );

  const { grouped, rows } = useUpgradeChipRows(upgrades);
  const { playerRoles } = useSelector(playersSelector);
  const metaRatingAfter =
    grouped.metaRating?.metaRating.value ?? metaRatingBeforeApplyingUpgrade;
  const roleAfter =
    grouped.playerRole?.playerRoleId ?? playerRoleIdBeforeApplyingUpgrade;
  const tierBefore = getTier(
    playerRoles,
    metaRatingBeforeApplyingUpgrade,
    playerRoleIdBeforeApplyingUpgrade,
    true,
  );
  const tierAfter = getTier(playerRoles, metaRatingAfter, roleAfter, true);

  if (!evolution && !chainIds) return null;

  const covered = !!chainWall;
  const pressable = !covered && !loading;

  // The rating, and under it the role it is rated for when the step changes
  // that role. A chain card sets it under the player; a step card keeps it
  // top-right, across from the chain.
  const ratingUnderCard = isChain && !!player;
  // On a phone a chain runs along the bottom of the card, full width and
  // scrolling sideways, instead of squeezing in beside the player.
  const chainAtBottom = isChain && isMobile;
  const chain = isChain ? (
    <RecommendedChain
      evolutionIds={chainIds}
      pathwayPath={props.pathwayPath}
      onClickUnlockChains={onClickUnlockChains}
      chainWall={chainWall}
    />
  ) : (
    <EvoChainFromIds
      evolutionIds={[evolutionId]}
      pathwayPath={[props.pathwayPath ?? []]}
    />
  );
  const rating = (
    <div
      className={cn(
        "flex shrink-0 flex-col gap-1",
        ratingUnderCard ? "items-center" : "items-end",
        loading && "invisible",
      )}
    >
      <MetaRatingChip
        metaRating={metaRatingAfter}
        tier={tierAfter}
        basePlayerMetaRating={metaRatingBeforeApplyingUpgrade}
        basePlayerTier={tierBefore ?? undefined}
      />
      {grouped.playerRole && (
        <RoleChangeHint playerRoleId={grouped.playerRole.playerRoleId} />
      )}
    </div>
  );

  return (
    <Card
      hoverable={pressable}
      className={cn(
        "flex h-full w-full flex-col gap-3 p-3",
        pressable ? "cursor-pointer" : "cursor-default",
        loading && "animate-pulse",
        className,
      )}
    >
      <div
        role={pressable ? "button" : undefined}
        tabIndex={pressable ? 0 : undefined}
        onClick={() => pressable && onClick()}
        onKeyDown={(e) => {
          if (!pressable) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className="flex h-full min-w-0 flex-col gap-3 outline-none"
      >
        <div className="flex min-w-0 flex-1 gap-3">
          {player && (
            <div className="flex shrink-0 flex-col items-center gap-2 self-start -mt-6">
              <PlayerCard
                player={player}
                position={player.preferredPosition}
                scale={CARD_SCALE[size]}
                isEvolution
                hidePrice
              />
              {ratingUnderCard && rating}
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            {/* The chip wraps under the chain when a narrow card can't fit both. */}
            {(!chainAtBottom || !ratingUnderCard) && (
              <div className="flex flex-wrap items-center justify-between gap-2">
                {!chainAtBottom && (
                  <div className="min-w-0 flex-1 basis-44">{chain}</div>
                )}
                {!ratingUnderCard && rating}
              </div>
            )}

            {tags && tags.length > 0 && (
              <ul className="flex flex-wrap items-center gap-1">
                {tags.map((tag) => (
                  <li key={tag}>
                    <EvolutionChainTag tag={tag} />
                  </li>
                ))}
              </ul>
            )}

            {/* One row that scrolls sideways rather than wrapping, with the
                same edge shadows as the chain. */}
            <ScrollArea className="w-full" enableShadows blockClickOnScroll>
              {/* Radix only lets the viewport scroll on the axes that have a
                  scrollbar mounted; without this the row is overflow-x hidden
                  and a finger cannot move it. Zero height: the shadows are
                  the affordance here. */}
              <ScrollBar orientation="horizontal" className="h-0 border-0" />
              <div className="flex items-center gap-1.5 [&>*]:shrink-0">
                <Pill
                  icon={<CoinIcon size={14} />}
                  label={
                    cost?.coins > 0
                      ? formatEvoCost(cost.coins)
                      : cost?.points > 0 || tokenCosts.length > 0
                        ? undefined
                        : t("common:free")
                  }
                  hidden={!(cost?.coins > 0) && cost?.points > 0}
                />
                {cost?.points > 0 && (
                  <Pill
                    icon={
                      <img
                        alt={t("common:alt.points")}
                        className="size-3.5"
                        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/fc24/misc/points.png`}
                      />
                    }
                    label={String(cost.points)}
                  />
                )}
                {tokenCosts.map((token) => (
                  <Pill
                    key={token.eventTokenId}
                    icon={
                      <img
                        alt={token.name}
                        className="size-3.5 object-contain"
                        src={token.iconUrl}
                      />
                    }
                    label={String(token.amount)}
                  />
                ))}
                {requiredGames > 0 && (
                  <Pill
                    icon={
                      <img
                        alt={t("evolutions:alt.matches")}
                        className="size-3.5"
                        src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/ea/ball.png`}
                      />
                    }
                    label={t("card.games", { count: requiredGames })}
                  />
                )}
                {evolution?.repeats > 0 && (
                  <Pill
                    icon={<span className="size-3">{repeatable}</span>}
                    label={String(evolution.repeats)}
                  />
                )}
                {evolution?.expired ? (
                  <Pill label={t("evolutions:expired")} muted />
                ) : (
                  timeRemaining && (
                    <Pill
                      icon={
                        <span className="size-3 fill-current">
                          {expireIcon}
                        </span>
                      }
                      label={timeRemaining}
                    />
                  )
                )}
              </div>
            </ScrollArea>

            <div className="mobile:mt-auto flex-1 mobile:flex-none flex flex-col gap-1.5 rounded-xl bg-background p-2.5">
              {rows.length > 0 ? (
                rows.map((row, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-1.5">
                    {row}
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-1 text-xs text-foreground-muted">
                  <InfoIcon
                    width="12"
                    height="12"
                    outerFill="var(--color-foreground-muted)"
                  />
                  {t("card.noUpgrades")}
                </div>
              )}
            </div>
          </div>
        </div>
        {chainAtBottom && <div className="min-w-0">{chain}</div>}
      </div>
    </Card>
  );
};

/** A small fact about the step: its price, the games it takes, its expiry. */
const Pill = ({
  icon,
  label,
  muted = false,
  hidden = false,
}: {
  icon?: ReactNode;
  label?: ReactNode;
  muted?: boolean;
  hidden?: boolean;
}) => {
  if (hidden || label === undefined) return null;
  return (
    <span
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-lg border border-edge-strong bg-surface-raised px-2 text-xs whitespace-nowrap",
        muted ? "text-foreground-muted" : "text-foreground-body",
      )}
    >
      {icon && (
        <span className="inline-flex shrink-0 items-center text-foreground-muted">
          {icon}
        </span>
      )}
      {label}
    </span>
  );
};

/** A recommended chain's chips, covered by the login / reveal wall if any. */
const RecommendedChain = ({
  evolutionIds,
  pathwayPath,
  onClickUnlockChains,
  chainWall,
}: {
  evolutionIds: number[];
  pathwayPath?: number[][];
  onClickUnlockChains?: () => void;
  chainWall?: ChainWallMode;
}) =>
  chainWall ? (
    // A band tall enough to read as a button, over the withheld chain blown
    // up behind it so the blur has colour to work with.
    <div className="relative h-14">
      {onClickUnlockChains && (
        <div className="absolute inset-0 z-1">
          <ChainWall mode={chainWall} onClick={onClickUnlockChains} />
        </div>
      )}
      <div
        aria-hidden
        className="pointer-events-none flex h-full select-none items-center overflow-hidden"
      >
        <div className="origin-left scale-150">
          <EvoChainFromIds evolutionIds={evolutionIds} />
        </div>
      </div>
    </div>
  ) : (
    <EvoChainFromIds
      evolutionIds={evolutionIds}
      pathwayPath={pathwayPath}
      layout="scroll"
      reserveScrollbarSpace={false}
    />
  );

const RoleChangeHint = ({ playerRoleId }: { playerRoleId: number }) => {
  const { t } = useTranslation("evoBuilder");
  const playerRoleName = useSelector(
    playerRoleLabelSelector({ playerRoleId, shortName: true }),
  );
  return (
    <Tooltip
      content={
        <div className="w-64 text-sm text-foreground-body">
          {t("card.roleChangeHint", { role: playerRoleName })}
        </div>
      }
      delayDuration={100}
    >
      <div className="flex items-center gap-1 text-xs text-foreground-muted">
        <InfoIcon
          width="12"
          height="12"
          innerFill="var(--color-foreground-body)"
          outerFill="var(--color-control)"
        />
        <span className="whitespace-nowrap">{playerRoleName}</span>
      </div>
    </Tooltip>
  );
};

export const EvolutionChainTag = ({ tag }: { tag: EvolutionChainTagType }) => {
  const { t } = useTranslation("evoBuilder");
  const colors = getEvolutionChainTagColor(tag);
  return (
    <span
      className={cn(
        "inline-flex h-5 items-center rounded-full border px-2 text-xs font-semibold whitespace-nowrap",
        colors.backgroundColor,
        colors.borderColor,
        colors.textColor,
      )}
    >
      {chainTagLabel(tag, t)}
    </span>
  );
};
